<template>
    <div class="h-full">
        <Card class="h-full">
            <template #title>
                <div class="flex items-center gap-2">
                    <Icon
                        name="clock"
                        class="w-4 h-4 text-gray-500"
                    />
                    <span class="truncate font-semibold text-dark">
                        {{ availability.name }}
                    </span>
                </div>
            </template>
            <template #content>
                <div class="border-b border-b-gray-200 mb-4"></div>

                <div class="text-sm space-y-4">
                    <div class="flex items-start justify-between">
                        <div>Timezone</div>
                        <div>
                            {{ availability.timeZone }}
                        </div>
                    </div>

                    <div class="space-y-2">
                        <div v-for="entry in availability.weeklyHours">
                            <div class="flex items-start justify-between">
                                <div>
                                    {{ entry.dayOfWeek }}
                                </div>
                                <div>
                                    <div v-for="timeRange in entry.timeRanges">
                                        <span>{{ timeRange.start }}</span>
                                        -
                                        <span>{{ timeRange.end }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template #actions>
                <div class="flex flex-col w-full gap-1">
                    <MakeAvailabilityDefaultButtonModal
                        v-if="companyUser.isAdminOrOwner()"
                        :isDefault="
                            defaultAvailabilityId?.value ===
                            availability.id.value
                        "
                        :availability="availability"
                        @onSuccess="
                            emits('onDefaultAvailabilityUpdated', $event)
                        "
                    />
                    <Button
                        as="a"
                        :href="
                            urlFactory.makeAvailabilityEditPageUrl(
                                availability.id,
                            )
                        "
                        color="light"
                        iconName="edit"
                        size="small"
                    >
                        Edit
                    </Button>
                    <DeleteAvailabilityButtonModal
                        v-if="availability.canBeManagedBy(companyUser)"
                        :isDefault="
                            defaultAvailabilityId?.value ===
                            availability.id.value
                        "
                        :availability="availability"
                        @onSuccess="emits('onAvailabilityDeleted')"
                    />
                </div>
            </template>
        </Card>
    </div>
</template>

<script setup lang="ts">
    import { UrlFactory } from '@/Shared/Url/UrlFactory'
    import type { PropType } from 'vue'
    import Button from '@/Shared/Component/Button.vue'
    import Card from '@/Shared/Component/Card.vue'
    import Icon from '@/Shared/Component/Icon.vue'
    import Company from '@/App/Company/Entity/Company'
    import CompanyUser from '@/App/CompanyUser/Entity/CompanyUser'
    import Availability from '@/App/Availability/Entity/Availability'
    import AvailabilityID from '@/App/Availability/Entity/AvailabilityID'
    import MakeAvailabilityDefaultButtonModal from '@/App/Availability/Component/MakeAvailabilityDefaultButtonModal.vue'
    import DeleteAvailabilityButtonModal from '@/App/Availability/Component/DeleteAvailabilityButtonModal.vue'

    const emits = defineEmits<{
        onAvailabilityUpdated: [{ availability: Availability }]
        onAvailabilityDeleted: []
        onDefaultAvailabilityUpdated: [{ company: Company }]
    }>()

    const props = defineProps({
        defaultAvailabilityId: {
            type: Object as PropType<AvailabilityID | null>,
            default: null,
        },
        availability: {
            type: Object as PropType<Availability>,
            required: true,
        },
        companyUser: {
            type: Object as PropType<CompanyUser>,
            required: true,
        },
    })
    const urlFactory = new UrlFactory()
</script>
